import { DIRECTORY_IMPORT_MAX_BYTES, type DirectoryImport, ERROR_CODES } from "@procuremate/shared";
import { Upload } from "lucide-react";
import { type FormEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";
import { Button } from "@/components/ui/button";
import { TextField } from "@/components/ui/text-field";
import { ApiRequestError } from "@/lib/api-client";
import { Card } from "./admin-layout";
import { EMPLOYEES_PAGE_SIZE, useDirectoryImports, useEmployees, useImportDirectory } from "./api";

const SEARCH_DELAY_MS = 300;
/** Problems listed on screen after an import; the rest are counted. */
const PROBLEMS_SHOWN = 20;

/**
 * Admin → Directory (WP-2.5, ADR-0021): import the HR export, see what each import did, and look up
 * a person to check what the system will use for their requests.
 */
export function DirectoryPage() {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-6">
      <Card title={t("admin.directory.title")} headingLevel={1}>
        <p className="mb-4 max-w-prose text-sm text-fg-muted">{t("admin.directory.intro")}</p>
        <ImportForm />
      </Card>
      <RecentImports />
      <People />
    </div>
  );
}

function ImportForm() {
  const { t } = useTranslation();
  const mutation = useImportDirectory();
  const [file, setFile] = useState<File | null>(null);
  const [inputKey, setInputKey] = useState(0);
  const [localError, setLocalError] = useState<string | undefined>();

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!file) {
      setLocalError(t("admin.directory.chooseFile"));
      return;
    }
    if (file.size > DIRECTORY_IMPORT_MAX_BYTES) {
      setLocalError(t("admin.directory.refused.too_large"));
      return;
    }
    setLocalError(undefined);
    mutation.mutate(file, {
      onSuccess: () => {
        setFile(null);
        setInputKey((k) => k + 1);
      },
    });
  }

  const error = localError ?? (mutation.isError ? importErrorText(mutation.error, t) : undefined);
  return (
    <div className="flex flex-col gap-4">
      <form onSubmit={submit} noValidate className="flex flex-col gap-3 sm:flex-row sm:items-end">
        <TextField
          key={inputKey}
          id="directory-file"
          type="file"
          accept=".csv,.tsv,.txt,text/csv,text/tab-separated-values,text/plain"
          label={t("admin.directory.file")}
          error={error}
          className="sm:flex-1 [&_input]:py-2"
          onChange={(event) => {
            setFile(event.target.files?.[0] ?? null);
            setLocalError(undefined);
            mutation.reset();
          }}
        />
        <Button type="submit" disabled={mutation.isPending} className={error ? "sm:mb-7" : undefined}>
          <Upload aria-hidden="true" className="h-4 w-4" />
          {mutation.isPending ? t("admin.directory.importing") : t("admin.directory.import")}
        </Button>
      </form>
      <div aria-live="polite">{mutation.data ? <ImportResult result={mutation.data} /> : null}</div>
    </div>
  );
}

function importErrorText(error: unknown, t: ReturnType<typeof useTranslation>["t"]): string {
  if (error instanceof ApiRequestError) {
    if (error.status === 413) return t("admin.directory.refused.too_large");
    if (error.code === ERROR_CODES.VALIDATION_FAILED) {
      const details = error.details as { reason?: string; columns?: string[] } | undefined;
      if (details?.reason === "missing_columns") {
        return t("admin.directory.refused.missing_columns", { columns: (details.columns ?? []).join(", ") });
      }
      if (details?.reason)
        return t(`admin.directory.refused.${details.reason}`, { defaultValue: t("admin.errors.validation") });
    }
    if (error.code === ERROR_CODES.FORBIDDEN) return t("admin.errors.forbidden");
  }
  return t("auth.genericError");
}

function ImportResult({ result }: { result: DirectoryImport }) {
  const { t } = useTranslation();
  const shown = result.problems.slice(0, PROBLEMS_SHOWN);
  return (
    <section
      aria-labelledby="import-result-title"
      className="rounded-md border border-border bg-bg p-4 text-sm"
      data-testid="import-result"
    >
      <h2 id="import-result-title" className="font-semibold text-fg">
        {t("admin.directory.resultTitle", { file: result.fileName })}
      </h2>
      <p className="mt-1 text-fg">{t("admin.directory.counts", { ...result, count: result.rowCount })}</p>
      {result.encoding === "windows-1252" ? (
        <p className="mt-2 text-fg-muted">{t("admin.directory.windowsEncoding")}</p>
      ) : null}
      {result.unknownColumns.length > 0 ? (
        <p className="mt-2 text-fg-muted">
          {t("admin.directory.unknownColumns", { columns: result.unknownColumns.join(", ") })}
        </p>
      ) : null}
      {shown.length > 0 ? (
        <>
          <h3 className="mt-3 font-semibold text-fg">{t("admin.directory.problemsTitle")}</h3>
          <ul className="mt-1 list-disc pl-5 text-fg">
            {shown.map((p) => (
              <li key={`${p.row}-${p.code}`}>{t(`admin.directory.problems.${p.code}`, { row: p.row })}</li>
            ))}
          </ul>
          {result.problems.length > shown.length ? (
            <p className="mt-1 text-fg-muted">
              {t("admin.directory.moreProblems", { count: result.problems.length - shown.length })}
            </p>
          ) : null}
        </>
      ) : null}
    </section>
  );
}

function RecentImports() {
  const { t, i18n } = useTranslation();
  const imports = useDirectoryImports();
  const dateFormat = new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium", timeStyle: "short" });
  return (
    <Card title={t("admin.directory.recent")}>
      {imports.isError ? (
        <p role="alert" className="rounded-sm bg-danger-bg px-3 py-2 text-sm text-danger">
          {t("auth.genericError")}
        </p>
      ) : null}
      {imports.data && imports.data.length === 0 ? (
        <p className="text-sm text-fg-muted">{t("admin.directory.noImports")}</p>
      ) : null}
      {imports.data && imports.data.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <caption className="sr-only">{t("admin.directory.recent")}</caption>
            <thead className="border-b border-border text-fg-muted">
              <tr>
                <th scope="col" className="py-2 pr-4 font-semibold">
                  {t("admin.directory.when")}
                </th>
                <th scope="col" className="py-2 pr-4 font-semibold">
                  {t("admin.directory.result")}
                </th>
                <th scope="col" className="hidden py-2 font-semibold md:table-cell">
                  {t("admin.directory.by")}
                </th>
              </tr>
            </thead>
            <tbody>
              {imports.data.map((i) => (
                <tr key={i.id} className="border-b border-border last:border-0">
                  <td className="py-3 pr-4 align-top">
                    <div>{dateFormat.format(new Date(i.createdAt))}</div>
                    <div className="break-all text-fg-muted">{i.fileName}</div>
                  </td>
                  <td className="py-3 pr-4 align-top">{t("admin.directory.counts", { ...i, count: i.rowCount })}</td>
                  <td className="hidden py-3 align-top md:table-cell">{i.importedBy ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </Card>
  );
}

function People() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const q = params.get("q") ?? "";
  const page = Math.max(1, Number(params.get("page")) || 1);
  const [search, setSearch] = useState(q);

  // Typing updates the URL after a short pause (one request per pause, not per keystroke).
  useEffect(() => {
    if (search.trim() === q) return;
    const timer = setTimeout(() => update({ q: search.trim(), page: "" }), SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  });

  function update(changes: Record<string, string>) {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(changes)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }
    setParams(next, { replace: true });
  }

  const people = useEmployees({ q, page });
  const total = people.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / EMPLOYEES_PAGE_SIZE));
  const none = "—";

  return (
    <Card title={t("admin.directory.people")}>
      <TextField
        id="employee-search"
        className="mb-4"
        label={t("admin.directory.search")}
        type="search"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
      />
      {people.isError ? (
        <p role="alert" className="rounded-sm bg-danger-bg px-3 py-2 text-sm text-danger">
          {t("auth.genericError")}
        </p>
      ) : null}
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <caption className="sr-only">{t("admin.directory.people")}</caption>
          <thead className="border-b border-border text-fg-muted">
            <tr>
              <th scope="col" className="py-2 pr-4 font-semibold">
                {t("admin.directory.person")}
              </th>
              <th scope="col" className="hidden py-2 pr-4 font-semibold md:table-cell">
                {t("admin.directory.organisation")}
              </th>
              <th scope="col" className="hidden py-2 pr-4 font-semibold sm:table-cell">
                {t("admin.directory.businessOwner")}
              </th>
              <th scope="col" className="hidden py-2 font-semibold sm:table-cell">
                {t("admin.directory.secondLevelManager")}
              </th>
            </tr>
          </thead>
          <tbody>
            {people.data?.items.map((p) => (
              <tr key={p.id} className="border-b border-border last:border-0">
                <td className="py-3 pr-4 align-top">
                  <div className="font-semibold text-fg">{p.fullName}</div>
                  <div className="break-all text-fg-muted">{p.email ?? t("admin.directory.noEmail")}</div>
                  {p.lineManager ? (
                    <div className="text-fg-muted">{t("admin.directory.lineManager", { name: p.lineManager })}</div>
                  ) : null}
                  {/* On phones the manager columns are hidden: show them here instead. */}
                  <dl className="mt-1 grid grid-cols-[auto_1fr] gap-x-2 text-fg-muted sm:hidden">
                    <dt>{t("admin.directory.businessOwner")}</dt>
                    <dd className="text-fg">{p.businessOwner ?? none}</dd>
                    <dt>{t("admin.directory.secondLevelManager")}</dt>
                    <dd className="text-fg">{p.secondLevelManager ?? none}</dd>
                  </dl>
                </td>
                <td className="hidden py-3 pr-4 align-top md:table-cell">
                  <div>{p.company ?? none}</div>
                  <div className="text-fg-muted">{[p.businessUnit, p.role].filter(Boolean).join(" · ") || none}</div>
                </td>
                <td className="hidden py-3 pr-4 align-top sm:table-cell">{p.businessOwner ?? none}</td>
                <td className="hidden py-3 align-top sm:table-cell">{p.secondLevelManager ?? none}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {people.data && people.data.items.length === 0 ? (
        <p className="py-6 text-center text-fg-muted">
          {q ? t("admin.directory.noMatch") : t("admin.directory.empty")}
        </p>
      ) : null}
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm text-fg-muted">
        <p aria-live="polite">{t("admin.directory.count", { count: total })}</p>
        {pages > 1 ? (
          <div className="flex items-center gap-2">
            <Button variant="secondary" disabled={page <= 1} onClick={() => update({ page: String(page - 1) })}>
              {t("admin.users.previous")}
            </Button>
            <span>{t("admin.users.pageOf", { page, pages })}</span>
            <Button variant="secondary" disabled={page >= pages} onClick={() => update({ page: String(page + 1) })}>
              {t("admin.users.next")}
            </Button>
          </div>
        ) : null}
      </div>
    </Card>
  );
}
